import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import DateRangeSelector from "../DateRangeSelector";
import DynamicTable from "../lab-dynamic-table";
import { formatCurrency } from "../../utils/numberUtils";
import { fetchProfileData } from "../../redux/reducers/profileSlice";
import {
  Print,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "@mui/material";

interface Transaction {
  _id: string;
  nowPaying: number;
  dueAmount: number;
  status: string;
  createdAt: string;
  collectionCenterName: string;
  mrn: number;
  patientName: string;
  phonNumber: string;
  trNo: number;
  paymentStatus: string;
  isPendingDues: boolean;
  registeredAt: string;
  registeredBy: {
    _id: string;
    fullName?: string;
    firstName?: string;
  };
  receivedBy: string;
}

interface Expense {
  _id: string;
  description: string;
  amount: number;
  paymentStatus: string;
  createdAt: string;
  paymentDate: string;
  createdBy: {
    _id: string;
    firstName?: string;
    fullName?: string;
  };
  category: {
    _id: string;
    name: string;
    description: string;
  };
}

interface IncomeExpenseSummaryData {
  transaction: Transaction[];
  expense: Expense[];
  instantAmount: number;
  pendingDueAmount: number;
  totalIncome: number;
  totalExpense: number;
  netAmount: number;
  transactionCount: number;
  expenseCount: number;
  pendingDueCount: number;
  instantCount: number;
}

const IncomeExpenseSummaryTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IncomeExpenseSummaryData | null>(null);
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [showTransactionTable, setShowTransactionTable] = useState(false);
  const [showExpenseTable, setShowExpenseTable] = useState(false);

  // New States for filters
  const [users, setUsers] = useState<any[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] = useState<string>("all");

  const dispatch = useDispatch();
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const collectionCenters = profile?.data?.collectionCenter || [];
  const labPermissions = profile?.data?.accessConfigurations?.Lab || {};

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getHospitalAllUsers`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setUsers(response.data?.data || []);
    } catch (error: any) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    dispatch(fetchProfileData() as any);
  }, [dispatch]);

  // Pre-select login user if permission to view all reports is false
  useEffect(() => {
    if (profile?.data?._id && labPermissions && labPermissions.can_view_all_users_reports === false) {
      setUserId(profile.data._id);
    }
  }, [profile, labPermissions]);

  const fetchIncomeExpenseSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/dashboard/getIncomeExpenseSummary`,
        {
          params: {
            fromDate,
            toDate,
            ...(userId && { userId }),
            ...(selectedCollectionCenterId !== "all" && { collectionCenterId: selectedCollectionCenterId }),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.isSuccess) {
        setData(response.data.data);
      } else {
        toast.error(response.data.message || "Failed to fetch income expense summary");
      }
    } catch (error: any) {
      console.error("Error fetching income expense summary:", error);
      toast.error(error.response?.data?.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncomeExpenseSummary();
  }, [fromDate, toDate, userId, selectedCollectionCenterId]);

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
  };

  const handleFromDateChange = (date: string) => {
    setFromDate(date);
  };

  const handleToDateChange = (date: string) => {
    setToDate(date);
  };

  const handlePrint = () => {
    if (!data) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = 25;

    // Color Palette (Professional Corporate)
    const colors: Record<string, [number, number, number]> = {
      primary: [25, 55, 109],      // Dark Blue
      secondary: [63, 81, 181],    // Medium Blue
      accent: [244, 245, 247],     // Light Gray
      dark: [33, 33, 33],          // Dark Gray
      border: [200, 204, 214],     // Light Border
      white: [255, 255, 255]
    };

    // ===== HEADER SECTION =====
    // Subtle header background
    doc.setFillColor(...colors.accent);
    doc.rect(margin - 5, 15, pageWidth - 2 * margin + 10, 25, 'F');

    // Main Title
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("Income & Expense Summary Report", pageWidth / 2, 28, { align: "center" });

    yPos = 50;

    // ===== FILTER INFORMATION =====
    const selectedUser = users.find(u => u._id === userId);
    const userNameDisplay = selectedUser ? (selectedUser.fullName || selectedUser.firstName || "Unknown User") : "ALL USERS";

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.dark);

    const filterInfo = [
      { label: "User:", value: userNameDisplay.toUpperCase() },
      { label: "Period:", value: `${fromDate} to ${toDate}` }
    ];

    filterInfo.forEach((item, idx) => {
      const xPos = margin + (idx * (pageWidth - 2 * margin) / 2);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...colors.secondary);
      doc.text(item.label, xPos, yPos);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.dark);
      doc.text(item.value, xPos + 18, yPos);
    });

    yPos += 15;

    // ===== TRANSACTION SUMMARY TABLE =====
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("Transaction Summary", margin, yPos);
    yPos += 8;

    autoTable(doc, {
      startY: yPos,
      margin: { left: margin, right: margin },
      head: [["SN", "Tr No", "Patient Name", "Register By", "Register At", "Amount", "Status", "Received By", "Received At"]],
      body: data.transaction.map((tx, i) => [
        i + 1,
        tx.trNo,
        tx.patientName,
        tx.registeredBy?.fullName || tx.registeredBy?.firstName || tx.receivedBy || "-",
        formatDateTime(tx.registeredAt),
        formatCurrency((tx.nowPaying || 0) + (tx.dueAmount || 0)),
        tx.isPendingDues ? "Previous Dues" : "Instant",
        tx.receivedBy || "-",
        formatDateTime(tx.createdAt)
      ]),
      headStyles: {
        fillColor: [220, 225, 230], // Light Gray BG
        textColor: [31, 41, 55],    // Dark Gray Text
        fontStyle: 'bold',
        fontSize: 7.5,
        cellPadding: 1
      },
      bodyStyles: {
        fontSize: 7,
        textColor: colors.dark
      },
      styles: {
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [200, 204, 214],
        halign: 'left',
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' }, // SN
        1: { cellWidth: 12, halign: 'center' }, // Tr No
        4: { cellWidth: 18 }, // Register At
        8: { cellWidth: 18 }  // Received At
      },
      didDrawPage: (dataArg) => {
        yPos = dataArg.cursor ? dataArg.cursor.y + 7 : yPos + 7;
      }
    });

    // ===== PAGE BREAK CHECK =====
    if (yPos > pageHeight - 80) {
      doc.addPage();
      yPos = 20;
    }

    // ===== EXPENSE SUMMARY TABLE =====
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("Expense Summary", margin, yPos);
    yPos += 8;

    autoTable(doc, {
      startY: yPos,
      margin: { left: margin, right: margin },
      head: [["SN", "Description", "Amount", "Paid By", "Paid At"]],
      body: data.expense.map((exp, i) => [
        i + 1,
        exp.description || exp.category.description || "-",
        formatCurrency(exp.amount),
        exp.createdBy.fullName || exp.createdBy.firstName || "-",
        formatDateTime(exp.paymentDate)
      ]),
      headStyles: {
        fillColor: [220, 225, 230], // Light Gray BG
        textColor: [31, 41, 55],    // Dark Gray Text
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 8.5,
        textColor: colors.dark
      },
      styles: {
        cellPadding: 2,
        lineWidth: 0.2,
        lineColor: [200, 204, 214],
        halign: 'left',
        valign: 'middle'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { halign: 'left' }
      },
      didDrawPage: (dataArg) => {
        yPos = dataArg.cursor ? dataArg.cursor.y + 7 : yPos + 7;
      }
    });

    // ===== PAGE BREAK CHECK =====
    if (yPos > pageHeight - 100) {
      doc.addPage();
      yPos = 20;
    } else {
      yPos += 15;
    }

    // ===== SUMMARY SECTION (Side by Side) =====
    const summaryStartY = yPos;
    const colWidth = (pageWidth - 3 * margin) / 2;

    // Financial Summary (Left)
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("Financial Summary", margin, summaryStartY);

    const financialData = [
      ["Instant Amount", formatCurrency(data.instantAmount)],
      ["Due Amount", formatCurrency(data.pendingDueAmount)],
      ["Total Income", formatCurrency(data.totalIncome)],
      ["Total Expense", formatCurrency(data.totalExpense)],
      ["Net Amount", formatCurrency(data.netAmount)],
    ];

    autoTable(doc, {
      startY: summaryStartY + 6,
      margin: { left: margin },
      tableWidth: colWidth,
      head: [["Description", "Amount"]],
      body: financialData,
      theme: 'plain',
      headStyles: {
        textColor: colors.primary,
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 1,
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: colors.dark
      },
      styles: {
        cellPadding: 2,
        lineWidth: { bottom: 0.1 },
        lineColor: [180, 180, 180],
        halign: 'left',
        valign: 'middle'
      },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold' },
        1: { halign: 'left' }
      }
    });

    // Financial Counts (Right)
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("Financial Counts", margin + colWidth + margin, summaryStartY);

    const countsData = [
      ["Transaction Count", data.transactionCount.toString()],
      ["Expense Count", data.expenseCount.toString()],
      ["Pending Due Count", data.pendingDueCount.toString()],
      ["Instant Count", data.instantCount.toString()],
    ];

    autoTable(doc, {
      startY: summaryStartY + 6,
      margin: { left: margin + colWidth + margin },
      tableWidth: colWidth,
      head: [["Description", "Count"]],
      body: countsData,
      theme: 'plain',
      headStyles: {
        textColor: colors.primary,
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 1,
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: colors.dark
      },
      styles: {
        cellPadding: 2,
        lineWidth: { bottom: 0.1 },
        lineColor: [180, 180, 180],
        halign: 'left',
        valign: 'middle'
      },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold' },
        1: { halign: 'left' }
      }
    });

    // ===== FOOTER =====
    const footerY = pageHeight - 15;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} | Confidential`,
      pageWidth / 2,
      footerY,
      { align: "center" }
    );

    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    iframe.onload = () => {
      try {
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            iframe.contentWindow.onafterprint = () => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(blobUrl);
            };
          }
        }, 500);
      } catch (e) {
        console.error("Error triggering print:", e);
        window.open(blobUrl, "_blank");
        URL.revokeObjectURL(blobUrl);
      }
    };

    iframe.src = blobUrl;
  };


  const formatDateTime = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";

    const m = date.getMonth() + 1;
    const d = date.getDate();
    const y = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${m}/${d}/${y} ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const d = date.getDate();
    const m = months[date.getMonth()];
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const TableHeader = ({ headers }: { headers: string[] }) => (
    <thead>
      <tr style={{ backgroundColor: "#d1d5db", border: "1px solid #9ca3af" }}>
        {headers.map((header, index) => (
          <th
            key={index}
            style={{
              padding: "4px 12px",
              border: "1px solid #9ca3af",
              textAlign: "left",
              fontSize: "12px",
              fontWeight: "bold",
              color: "#1f2937",
              whiteSpace: "nowrap"
            }}
          >
            {header}
          </th>
        ))}
      </tr>
    </thead>
  );

  const TableCell = ({ children, align = "left", bold = false }: { children: React.ReactNode, align?: "left" | "right" | "center", bold?: boolean }) => (
    <td
      style={{
        padding: "4px 12px",
        border: "1px solid #9ca3af",
        textAlign: align,
        fontSize: "11px",
        color: "#374151",
        fontWeight: bold ? "bold" : "normal",
        whiteSpace: "nowrap"
      }}
    >
      {children}
    </td>
  );

  const transactionHeaders = ["SN", "Tr No", "Patient Name", "Register By", "Register At", "Amount", "Status", "Received By", "Received At"];
  const expenseHeaders = ["SN", "Description", "Amount", "Paid By", "Paid At"];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{
              textTransform: 'none',
              backgroundColor: '#077DD3',
              '&:hover': { backgroundColor: '#1e76b5ff' }
            }}
          >
            Print
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
          {/* Collection Center Filter */}
          <Box sx={{ minWidth: "200px" }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5, color: "#374151" }}>
              Collection Center
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                className="custom-input"
                value={selectedCollectionCenterId}
                onChange={(e: any) => setSelectedCollectionCenterId(e.target.value as string)}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="all">All Centers</MenuItem>
                {collectionCenters.map((center: any) => (
                  <MenuItem key={center._id} value={center._id}>
                    {center.collectionCenterName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Created By Filter */}
          <Box sx={{ minWidth: "200px" }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5, color: "#374151" }}>
              Created By
            </Typography>
            <Autocomplete
              className="custom-input"
              size="small"
              options={users}
              disabled={labPermissions.can_view_all_users_reports === false}
              getOptionLabel={(option: any) => option.fullName || option.firstName || ""}
              value={users.find((u: any) => u._id === userId) || null}
              onChange={(event: any, newValue: any) => setUserId(newValue ? newValue._id : "")}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  placeholder="Select User"
                  sx={{ backgroundColor: "white" }}
                />
              )}
            />
          </Box>

          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDateChange}
            preset="lab"
          />
        </Box>
      </Box>

      {!data && !loading && (
        <Alert severity="info">No data available for the selected date range.</Alert>
      )}

      {data && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
            <Button
              variant="text"
              size="small"
              startIcon={showTransactionTable ? <VisibilityOff /> : <Visibility />}
              onClick={() => setShowTransactionTable(!showTransactionTable)}
              sx={{
                color: '#077DD3',
                fontWeight: 600,
                fontSize: '12px',
                padding: '3px 6px',
                borderRadius: '6px',
                transition: 'all 0.2s',
                border: '1px solid #077DD3',
                '&:hover': {
                  backgroundColor: 'rgba(5, 85, 235, 0.05)',
                  color: '#077DD3'
                }
              }}
            >
              {showTransactionTable ? "Hide Transaction Detail" : "See Detail Transaction"}
            </Button>
            <Button
              variant="text"
              size="small"
              startIcon={showExpenseTable ? <VisibilityOff /> : <Visibility />}
              onClick={() => setShowExpenseTable(!showExpenseTable)}
              sx={{
                color: '#077DD3',
                fontWeight: 600,
                fontSize: '12px',
                padding: '3px 6px',
                borderRadius: '6px',
                transition: 'all 0.2s',
                border: '1px solid #077DD3',
                '&:hover': {
                  backgroundColor: 'rgba(5, 85, 235, 0.05)',
                  color: '#077DD3'
                }
              }}
            >
              {showExpenseTable ? "Hide Expense Summary" : "See Expense Summary"}
            </Button>
          </Box>

          <Grid container spacing={2}>
            {/* Transactions Table */}
            {showTransactionTable && (
              <Grid item xs={12} md={showExpenseTable ? 6 : 12}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold", color: "#1f2937", fontSize: "16px" }}>
                  Transaction Summary
                </Typography>
                <div style={{ overflowX: "auto", border: "1px solid #9ca3af", borderRadius: "2px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "white" }}>
                    <TableHeader headers={transactionHeaders} />
                    <tbody>
                      {data.transaction.map((tx, index) => (
                        <tr key={tx._id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{tx.trNo}</TableCell>
                          <TableCell>{tx.patientName}</TableCell>
                          <TableCell>{tx.registeredBy?.fullName || tx.registeredBy?.firstName || tx.receivedBy || "-"}</TableCell>
                          <TableCell>{formatDateTime(tx.registeredAt)}</TableCell>
                          <TableCell align="right">{formatCurrency((tx.nowPaying || 0) + (tx.dueAmount || 0))}</TableCell>
                          <TableCell>{tx.isPendingDues ? "Previous Dues" : "Instant"}</TableCell>
                          <TableCell>{tx.receivedBy || "-"}</TableCell>
                          <TableCell>{formatDateTime(tx.createdAt)}</TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Grid>
            )}

            {/* Expenses Table */}
            {showExpenseTable && (
              <Grid item xs={12} md={showTransactionTable ? 6 : 12}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold", color: "#1f2937", fontSize: "16px" }}>
                  Expense Summary
                </Typography>
                <div style={{ overflowX: "auto", border: "1px solid #9ca3af", borderRadius: "2px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "white" }}>
                    <TableHeader headers={expenseHeaders} />
                    <tbody>
                      {data.expense.map((exp, index) => (
                        <tr key={exp._id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{exp.description || exp.category.description || "-"}</TableCell>
                          <TableCell align="right">{formatCurrency(exp.amount)}</TableCell>
                          <TableCell>{exp.createdBy.fullName || exp.createdBy.firstName || "-"}</TableCell>
                          <TableCell>{formatDateTime(exp.paymentDate)}</TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Grid>
            )}
          </Grid>

          {/* Bottom Summary Section */}
          <Box sx={{ mt: 4, pt: 3, }}>
            <Grid container spacing={10}>
              {/* Financial Summary */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold", color: "#1f2937", fontSize: "14px" }}>
                  Financial Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "1px solid #1f2937", pb: 0.2 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>Instant Amount</Typography>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {formatCurrency(data.instantAmount)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "1px solid #1f2937", pb: 0.2 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>Due Amount</Typography>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {formatCurrency(data.pendingDueAmount)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "1px solid #1f2937", pb: 0.2 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>Total Amount</Typography>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {formatCurrency(data.totalIncome)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "1px solid #1f2937", pb: 0.2 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>Total Expense</Typography>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {formatCurrency(data.totalExpense)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "2px double #1f2937", pb: 0.2, mt: 0.5 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: "bold", color: "#111827", fontSize: "12px" }}>Net Amount</Typography>
                      <Typography sx={{ fontWeight: "bold", color: "#111827", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {formatCurrency(data.netAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Transaction Statistics */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold", color: "#1f2937", fontSize: "14px" }}>
                  Financial Counts
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "1px solid #1f2937", pb: 0.2 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>Transaction Count</Typography>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {data.transactionCount}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "1px solid #1f2937", pb: 0.2 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>Expense Count</Typography>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {data.expenseCount}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "1px solid #1f2937", pb: 0.2 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>Pending Due Count</Typography>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {data.pendingDueCount}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: "1px solid #1f2937", pb: 0.2 }}>
                    <Box sx={{ width: "160px", display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>Instant Count</Typography>
                      <Typography sx={{ fontWeight: 500, color: "#374151", fontSize: "12px" }}>:</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", textAlign: "right", fontSize: "13px" }}>
                        {data.instantCount}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

    </Box>
  );
};

export default IncomeExpenseSummaryTab;